import { useState, useEffect } from 'react';
import { adminAPI } from '../../lib/api';
import { Plus, Pencil, Trash2, X, Loader2, BookOpen, Calendar, ExternalLink, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';

export default function DirectoryAdminPage() {
  const [categories, setCategories] = useState([]);
  const [selectedCat, setSelectedCat] = useState(null);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [articlesLoading, setArticlesLoading] = useState(false);
  const [showCatForm, setShowCatForm] = useState(false);
  const [editingCat, setEditingCat] = useState(null);
  const [catForm, setCatForm] = useState({ name: '', description: '', keywords: [], is_active: true });
  const [keywordInput, setKeywordInput] = useState('');
  const [showArticleForm, setShowArticleForm] = useState(false);
  const [articleForm, setArticleForm] = useState({ title: '', content: '', cover_image: '', seo_title: '', seo_description: '', status: 'published' });
  const [searchFilter, setSearchFilter] = useState('');

  const loadCategories = () => {
    setLoading(true);
    adminAPI.getDirCategories().then(r => setCategories(r.data)).finally(() => setLoading(false));
  };

  const loadArticles = (catId) => {
    setArticlesLoading(true);
    adminAPI.getDirArticles(catId).then(r => {
      setArticles(r.data.articles || []);
    }).finally(() => setArticlesLoading(false));
  };

  useEffect(() => { loadCategories(); }, []);

  useEffect(() => {
    if (selectedCat) {
      loadArticles(selectedCat);
    } else {
      setArticles([]);
    }
  }, [selectedCat]);

  const resetCatForm = () => { setCatForm({ name: '', description: '', keywords: [], is_active: true }); setEditingCat(null); setShowCatForm(false); };

  const startEditCat = (cat) => {
    setCatForm({ name: cat.name, description: cat.description || '', keywords: cat.keywords || [], is_active: cat.is_active });
    setEditingCat(cat.id);
    setShowCatForm(true);
  };

  const addKeyword = () => {
    if (keywordInput.trim() && !catForm.keywords.includes(keywordInput.trim().toLowerCase())) {
      setCatForm(f => ({ ...f, keywords: [...f.keywords, keywordInput.trim().toLowerCase()] }));
      setKeywordInput('');
    }
  };

  const removeKeyword = (kw) => {
    setCatForm(f => ({ ...f, keywords: f.keywords.filter(k => k !== kw) }));
  };

  const handleCatSubmit = async () => {
    if (!catForm.name.trim()) { toast.error('Kategori adi zorunlu'); return; }
    try {
      if (editingCat) {
        await adminAPI.updateDirCategory(editingCat, catForm);
        toast.success('Kategori guncellendi');
      } else {
        await adminAPI.createDirCategory(catForm);
        toast.success('Kategori eklendi');
      }
      resetCatForm();
      loadCategories();
      if (selectedCat) loadArticles(selectedCat);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleCreateArticle = async () => {
    if (!articleForm.title.trim()) { toast.error('Baslik zorunlu'); return; }
    try {
      await adminAPI.createBlog(articleForm);
      toast.success('Makale olusturuldu');
      setShowArticleForm(false);
      setArticleForm({ title: '', content: '', cover_image: '', seo_title: '', seo_description: '', status: 'published' });
      if (selectedCat) loadArticles(selectedCat);
    } catch (err) { toast.error(err.response?.data?.detail || 'Hata'); }
  };

  const handleDeleteArticle = async (id) => {
    if (!window.confirm('Bu makaleyi silmek istediginize emin misiniz?')) return;
    try {
      await adminAPI.deleteBlog(id);
      toast.success('Makale silindi');
      if (selectedCat) loadArticles(selectedCat);
    } catch { toast.error('Silinemedi'); }
  };

  const filteredArticles = searchFilter
    ? articles.filter(a => a.title.toLowerCase().includes(searchFilter.toLowerCase()))
    : articles;

  const selectedCategory = categories.find(c => c.id === selectedCat);

  return (
    <div data-testid="admin-directory">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold">Sanayi Rehberi</h1>
          <p className="text-sm text-muted-foreground mt-1">Kategoriler ve eslesen blog makaleleri yonetimi</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => { resetCatForm(); setShowCatForm(true); }} data-testid="add-dir-cat-btn">
            <Plus className="h-4 w-4 mr-1" /> Kategori
          </Button>
        </div>
      </div>

      {/* Category Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map(cat => (
          <button
            key={cat.id}
            onClick={() => setSelectedCat(selectedCat === cat.id ? null : cat.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium transition-all ${
              selectedCat === cat.id
                ? 'bg-[hsl(221,83%,22%)] text-white border-[hsl(221,83%,22%)]'
                : 'bg-white text-foreground hover:border-slate-300'
            }`}
          >
            <BookOpen className="h-4 w-4" />
            {cat.name}
            <Badge variant={selectedCat === cat.id ? 'secondary' : 'outline'} className="text-xs ml-1">
              {cat.keywords?.length || 0} kw
            </Badge>
          </button>
        ))}
      </div>

      {/* Category Edit Form */}
      {showCatForm && (
        <Card className="mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{editingCat ? 'Kategori Duzenle' : 'Yeni Kategori'}</CardTitle>
            <button onClick={resetCatForm}><X className="h-5 w-5" /></button>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Kategori Adi *</Label><Input value={catForm.name} onChange={e => setCatForm(f => ({...f, name: e.target.value}))} data-testid="dir-cat-name" /></div>
              <div className="flex items-center gap-2 pt-6"><Switch checked={catForm.is_active} onCheckedChange={v => setCatForm(f => ({...f, is_active: v}))} /><Label>Aktif</Label></div>
            </div>
            <div><Label>Aciklama</Label><textarea value={catForm.description} onChange={e => setCatForm(f => ({...f, description: e.target.value}))} rows={2} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm" /></div>
            <div>
              <Label>Anahtar Kelimeler (makaleleri eslestirir)</Label>
              <div className="flex gap-2 mt-1">
                <Input value={keywordInput} onChange={e => setKeywordInput(e.target.value)} placeholder="Anahtar kelime ekle" onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyword())} />
                <Button variant="outline" onClick={addKeyword}>Ekle</Button>
              </div>
              <div className="flex flex-wrap gap-1 mt-2">
                {catForm.keywords.map(kw => (
                  <Badge key={kw} variant="secondary" className="cursor-pointer hover:bg-red-100" onClick={() => removeKeyword(kw)}>
                    {kw} <X className="h-3 w-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-1">Blog yazisi basliginda bu kelimelerden biri geciyorsa, o kategoride gorunur.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleCatSubmit} data-testid="dir-cat-submit">{editingCat ? 'Guncelle' : 'Kaydet'}</Button>
              <Button variant="outline" onClick={resetCatForm}>Iptal</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Selected Category Content */}
      {selectedCat && selectedCategory && (
        <>
          {/* Category Info Bar */}
          <Card className="mb-4">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="h-5 w-5 text-[hsl(221,83%,22%)]" />
                <div>
                  <h2 className="font-semibold">{selectedCategory.name}</h2>
                  <p className="text-xs text-muted-foreground">{selectedCategory.description}</p>
                </div>
                <Badge variant="secondary">{articles.length} makale</Badge>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => startEditCat(selectedCategory)}>
                  <Pencil className="h-4 w-4 mr-1" /> Duzenle
                </Button>
                <Button size="sm" onClick={() => setShowArticleForm(true)} data-testid="add-article-btn">
                  <Plus className="h-4 w-4 mr-1" /> Yeni Makale
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* New Article Form */}
          {showArticleForm && (
            <Card className="mb-4">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Yeni Makale Ekle</CardTitle>
                <button onClick={() => setShowArticleForm(false)}><X className="h-5 w-5" /></button>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  Baslikta su anahtar kelimelerden en az birini kullananinz: <strong>{selectedCategory.keywords?.join(', ')}</strong>
                </p>
                <div><Label>Baslik *</Label><Input value={articleForm.title} onChange={e => setArticleForm(f => ({...f, title: e.target.value}))} placeholder={`Orn: ${selectedCategory.name} ile ilgili rehber`} data-testid="article-title" /></div>
                <div><Label>Icerik (HTML)</Label><textarea value={articleForm.content} onChange={e => setArticleForm(f => ({...f, content: e.target.value}))} rows={6} className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm font-mono" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label>Kapak Gorseli URL</Label><Input value={articleForm.cover_image} onChange={e => setArticleForm(f => ({...f, cover_image: e.target.value}))} /></div>
                  <div><Label>SEO Title</Label><Input value={articleForm.seo_title} onChange={e => setArticleForm(f => ({...f, seo_title: e.target.value}))} /></div>
                </div>
                <div><Label>SEO Description</Label><Input value={articleForm.seo_description} onChange={e => setArticleForm(f => ({...f, seo_description: e.target.value}))} /></div>
                <div className="flex gap-2">
                  <Button onClick={handleCreateArticle} data-testid="article-submit">Makaleyi Olustur ve Yayinla</Button>
                  <Button variant="outline" onClick={() => setShowArticleForm(false)}>Iptal</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Article Search */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input value={searchFilter} onChange={e => setSearchFilter(e.target.value)} placeholder="Makale ara..." className="pl-9" />
          </div>

          {/* Articles List */}
          {articlesLoading ? (
            <div className="flex items-center justify-center h-32"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : filteredArticles.length === 0 ? (
            <Card><CardContent className="py-12 text-center text-muted-foreground">Bu kategoride makale bulunamadi</CardContent></Card>
          ) : (
            <div className="space-y-2">
              {filteredArticles.map(article => (
                <Card key={article.id} data-testid={`dir-article-${article.id}`}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      {article.cover_image ? (
                        <img src={article.cover_image} alt="" className="h-12 w-16 object-cover rounded flex-shrink-0" />
                      ) : (
                        <div className="h-12 w-16 bg-slate-100 rounded flex items-center justify-center flex-shrink-0">
                          <BookOpen className="h-5 w-5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <h3 className="font-medium text-sm truncate">{article.title}</h3>
                        <div className="flex items-center gap-3 mt-0.5">
                          <span className="text-xs text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {new Date(article.created_at).toLocaleDateString('tr-TR')}
                          </span>
                          <span className="text-xs font-mono text-muted-foreground">/blog/{article.slug}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <a href={`/blog/${article.slug}`} target="_blank" rel="noopener noreferrer">
                        <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                      </a>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteArticle(article.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </>
      )}

      {/* No category selected */}
      {!selectedCat && !loading && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <BookOpen className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p>Makaleleri goruntulemek icin bir kategori secin.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
